import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toolsAPI, calibrationsAPI, filesAPI, auditAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit, Trash2, Plus, Upload, FileText, Image,
  ExternalLink, CheckCircle, XCircle
} from 'lucide-react';
import AddToolDialog from '@/components/AddToolDialog';
import InternalSchedulesTab from '@/pages/InternalSchedulesTab';

const statusMap = {
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  recalled_for_calibration: { label: 'Recalled for Calibration', className: 'bg-red-50 text-red-700 border-red-200' },
  scrap: { label: 'Scrap', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  under_repair: { label: 'Under Repair', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  not_in_use: { label: 'Not In Use', className: 'bg-orange-50 text-orange-700 border-orange-200' },
};

const InfoField = ({ label, value, mono }) => (
  <div>
    <p className="text-[10px] uppercase text-slate-400 font-medium tracking-wider">{label}</p>
    <p className={`text-sm mt-0.5 ${mono ? 'font-mono' : ''} ${value ? 'text-[#0F172A]' : 'text-slate-300'}`}>
      {value || 'N/A'}
    </p>
  </div>
);

export default function ToolDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useAuth();

  const [tool, setTool] = useState(null);
  const [calibrations, setCalibrations] = useState([]);
  const [files, setFiles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [calDialogOpen, setCalDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  // Calibration form
  const [calForm, setCalForm] = useState({
    calibration_date: new Date().toISOString().substring(0, 10),
    performed_by: '',
    certification_no: '',
    standard_used: '',
    result: 'pass',
    notes: '',
  });
  const [calLoading, setCalLoading] = useState(false);

  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('certificate');
  const [uploadEntityType, setUploadEntityType] = useState('tool');
  const [uploadEntityId, setUploadEntityId] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [toolRes, calRes, filesRes, auditRes] = await Promise.all([
        toolsAPI.get(id),
        calibrationsAPI.list(id),
        filesAPI.list('tool', id),
        auditAPI.list({ entity_type: 'tool', entity_id: id, limit: 50 }),
      ]);
      setTool(toolRes.data);
      setCalibrations(calRes.data);
      setFiles(filesRes.data);
      setAuditLogs(auditRes.data);
    } catch (err) {
      toast.error('Failed to load instrument details');
      navigate('/tools');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this instrument?')) return;
    try {
      await toolsAPI.delete(id);
      toast.success('Instrument deleted');
      navigate('/tools');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    try {
      await toolsAPI.update(id, { status: newStatus });
      toast.success('Status updated');
      setStatusDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleCalSubmit = async (e) => {
    e.preventDefault();
    if (!calForm.calibration_date || !calForm.performed_by) {
      toast.error('Calibration Date and Performed By are required');
      return;
    }
    setCalLoading(true);
    try {
      await calibrationsAPI.create({
        tool_id: id,
        calibration_date: new Date(calForm.calibration_date).toISOString(),
        performed_by: calForm.performed_by,
        certification_no: calForm.certification_no,
        standard_used: calForm.standard_used,
        result: calForm.result,
        notes: calForm.notes,
      });
      toast.success('Calibration record added. Due date auto-calculated.');
      setCalDialogOpen(false);
      setCalForm({
        calibration_date: new Date().toISOString().substring(0, 10),
        performed_by: '',
        certification_no: '',
        standard_used: '',
        result: 'pass',
        notes: '',
      });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add calibration');
    } finally {
      setCalLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('entity_type', uploadEntityType);
      formData.append('entity_id', uploadEntityId || id);
      formData.append('file_category', uploadCategory);
      await filesAPI.upload(formData);
      toast.success('File uploaded. Previous files of same category marked as obsolete.');
      setUploadDialogOpen(false);
      setUploadFile(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-sm text-slate-400">Loading...</div>;
  }
  if (!tool) return null;

  const status = statusMap[tool.status] || statusMap.active;

  return (
    <div data-testid="tool-detail-page" className="space-y-4">
      {/* Breadcrumb & Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/tools')} className="gap-1" data-testid="back-to-tools">
            <ArrowLeft className="w-4 h-4" /> Instruments
          </Button>
          <span className="text-slate-300">/</span>
          <span className="text-sm font-medium text-[#0F172A]">{tool.instrument_name}</span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => setStatusDialogOpen(true)} className="text-xs gap-1" data-testid="change-status-button">
                Change Status
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="text-xs gap-1" data-testid="edit-tool-button">
                <Edit className="w-3 h-3" /> Edit
              </Button>
            </>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleDelete} className="text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-tool-button">
              <Trash2 className="w-3 h-3" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Tool Info Header */}
      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
            <InfoField label="Instrument Name" value={tool.instrument_name} />
            <InfoField label="Instrument Code" value={tool.instrument_code} mono />
            <InfoField label="Serial Number" value={tool.serial_number} mono />
            <div>
              <p className="text-[10px] uppercase text-slate-400 font-medium tracking-wider">Status</p>
              <Badge variant="outline" className={`mt-1 text-[10px] ${status.className}`} data-testid="tool-status-badge">
                {status.label}
              </Badge>
            </div>
            <InfoField label="Certification No." value={tool.certification_no} mono />
            <InfoField label="Range / Size" value={tool.range_size} />
            <InfoField label="Least Count" value={tool.least_count} />
            <InfoField label="Frequency" value={tool.frequency_days ? `${tool.frequency_days} days` : null} />
            <InfoField label="Calibration Date" value={tool.calibration_date?.substring(0, 10)} mono />
            <InfoField label="Due On" value={tool.due_on?.substring(0, 10) || tool.next_calibration_date?.substring(0, 10)} mono />
            <InfoField label="Location" value={tool.location} />
            <InfoField label="Method" value={tool.method} />
            <InfoField label="Acceptance Criteria" value={tool.acceptance_criteria} />
            <InfoField label="Standard for Criteria" value={tool.standard_used_for_criteria} />
            <InfoField label="Preferred Cal. Agency" value={tool.preferred_calibration_agency} />
            <InfoField label="Remark" value={tool.remark} />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="calibrations" className="w-full">
        <TabsList className="bg-slate-100/80" data-testid="tool-detail-tabs">
          <TabsTrigger value="calibrations" className="text-xs">Calibration History</TabsTrigger>
          <TabsTrigger value="internal" className="text-xs" data-testid="tab-internal-schedules">Internal Schedules</TabsTrigger>
          <TabsTrigger value="documents" className="text-xs">Documents</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs">Change Log</TabsTrigger>
        </TabsList>

        {/* Calibrations Tab */}
        <TabsContent value="calibrations" className="mt-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0F172A]">Calibration Records</h3>
            {canEdit && (
              <Button size="sm" onClick={() => setCalDialogOpen(true)} className="text-xs gap-1 bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="add-calibration-button">
                <Plus className="w-3 h-3" /> Add Record
              </Button>
            )}
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-500">Cal. Date</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Next Due</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Cert. No.</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Performed By</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Standard</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Result</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Recorded By</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[80px]">Files</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calibrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-6 text-sm text-slate-400">No calibration records</TableCell>
                  </TableRow>
                ) : calibrations.map(cal => (
                  <TableRow key={cal.id} data-testid={`cal-row-${cal.id}`}>
                    <TableCell className="font-mono text-xs">{cal.calibration_date?.substring(0, 10)}</TableCell>
                    <TableCell className="font-mono text-xs">{cal.next_due_date?.substring(0, 10)}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{cal.certification_no || '-'}</TableCell>
                    <TableCell className="text-xs">{cal.performed_by}</TableCell>
                    <TableCell className="text-xs text-slate-500">{cal.standard_used || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${cal.result === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : cal.result === 'fail' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {cal.result}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{cal.created_by_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {cal.certificate_file_id && (
                          <a
                            href={filesAPI.getUrl(cal.certificate_file_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline"
                            data-testid={`cal-cert-file-${cal.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FileText className="w-3 h-3" /> Cert
                          </a>
                        )}
                        {cal.standard_doc_file_id && (
                          <a
                            href={filesAPI.getUrl(cal.standard_doc_file_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline"
                            data-testid={`cal-std-file-${cal.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FileText className="w-3 h-3" /> Std
                          </a>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost" size="sm" className="h-6 text-[10px] gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUploadEntityType('calibration');
                              setUploadEntityId(cal.id);
                              setUploadDialogOpen(true);
                            }}
                            data-testid={`upload-for-cal-${cal.id}`}
                          >
                            <Upload className="w-3 h-3" /> Upload
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Internal Schedules Tab */}
        <TabsContent value="internal" className="mt-3">
          <InternalSchedulesTab toolId={id} onChanged={loadAll} />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0F172A]">Documents & Certificates</h3>
            {canEdit && (
              <Button size="sm" onClick={() => { setUploadEntityType('tool'); setUploadEntityId(''); setUploadDialogOpen(true); }} className="text-xs gap-1 bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="upload-document-button">
                <Upload className="w-3 h-3" /> Upload
              </Button>
            )}
          </div>
          {files.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No documents uploaded</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {files.map(f => {
                const isCurrent = f.doc_status === 'current';
                return (
                  <Card key={f.id} className={`border shadow-none hover:shadow-sm transition-shadow ${isCurrent ? 'border-slate-200' : 'border-slate-100 opacity-70'}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${isCurrent ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                          {f.content_type?.includes('pdf') ? (
                            <FileText className={`w-4 h-4 ${isCurrent ? 'text-red-500' : 'text-slate-400'}`} />
                          ) : (
                            <Image className={`w-4 h-4 ${isCurrent ? 'text-blue-500' : 'text-slate-400'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#0F172A] truncate">{f.filename}</p>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{f.file_category}</Badge>
                            {f.calibration_id && (
                              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                Cal {calibrations.find(c => c.id === f.calibration_id)?.calibration_date?.substring(0, 10) || ''}
                              </Badge>
                            )}
                            {isCurrent ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Current
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">
                                <XCircle className="w-2.5 h-2.5 mr-0.5" /> Obsolete
                              </Badge>
                            )}
                            <span className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(1)} KB</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">{f.uploaded_by_name} - {f.uploaded_at?.substring(0, 10)}</p>
                        </div>
                        <a
                          href={filesAPI.getUrl(f.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-[#0F172A]"
                          data-testid={`view-file-${f.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="mt-3">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Change Log</h3>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-500 w-[160px]">Timestamp</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[80px]">Action</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[120px]">User</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-sm text-slate-400">No log entries</TableCell>
                  </TableRow>
                ) : auditLogs.map(log => (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="font-mono text-[11px] text-slate-500">{log.timestamp?.replace('T', ' ').substring(0, 19)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{log.action}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{log.user_name}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-xs truncate">{log.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      {editOpen && (
        <AddToolDialog open={editOpen} onOpenChange={setEditOpen} onCreated={loadAll} editTool={tool} />
      )}

      {/* Change Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="change-status-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold">Change Instrument Status</DialogTitle>
            <DialogDescription className="text-xs">Select the new status</DialogDescription>
          </DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger className="h-9" data-testid="new-status-select">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="recalled_for_calibration">Recalled for Calibration</SelectItem>
              <SelectItem value="under_repair">Under Repair</SelectItem>
              <SelectItem value="not_in_use">Not In Use</SelectItem>
              <SelectItem value="scrap">Scrap</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setStatusDialogOpen(false)} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleStatusChange} className="text-xs bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="confirm-status-change">
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Calibration Dialog */}
      <Dialog open={calDialogOpen} onOpenChange={setCalDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-calibration-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold">Add Calibration Record</DialogTitle>
            <DialogDescription className="text-xs">
              Due date is auto-calculated from calibration date + frequency ({tool.frequency_days} days)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCalSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Calibration Date *</Label>
                <Input type="date" value={calForm.calibration_date} onChange={(e) => setCalForm(p => ({ ...p, calibration_date: e.target.value }))} className="h-8 text-sm" data-testid="cal-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Certification No.</Label>
                <Input value={calForm.certification_no} onChange={(e) => setCalForm(p => ({ ...p, certification_no: e.target.value }))} placeholder="Certificate number" className="h-8 text-sm font-mono" data-testid="cal-cert-no-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Performed By *</Label>
              <Input value={calForm.performed_by} onChange={(e) => setCalForm(p => ({ ...p, performed_by: e.target.value }))} placeholder="Technician / Agency name" className="h-8 text-sm" data-testid="cal-performed-by-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Standard Used</Label>
              <Input value={calForm.standard_used} onChange={(e) => setCalForm(p => ({ ...p, standard_used: e.target.value }))} placeholder="Reference standard" className="h-8 text-sm" data-testid="cal-standard-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Result</Label>
                <Select value={calForm.result} onValueChange={(val) => setCalForm(p => ({ ...p, result: val }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="cal-result-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="fail">Fail</SelectItem>
                    <SelectItem value="adjusted">Adjusted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Input value={calForm.notes} onChange={(e) => setCalForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes" className="h-8 text-sm" data-testid="cal-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setCalDialogOpen(false)} className="text-xs">Cancel</Button>
              <Button type="submit" size="sm" disabled={calLoading} className="text-xs bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="save-calibration-button">
                {calLoading ? 'Saving...' : 'Save Record'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold">Upload Document</DialogTitle>
            <DialogDescription className="text-xs">
              Uploading a new file of the same category will mark the previous one as obsolete
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="h-8 text-sm" data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="certificate">Calibration Certificate</SelectItem>
                  <SelectItem value="standard">Acceptance Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">File (PDF, PNG, JPG)</Label>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="h-8 text-sm"
                data-testid="upload-file-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setUploadDialogOpen(false)} className="text-xs">Cancel</Button>
              <Button type="submit" size="sm" disabled={uploadLoading} className="text-xs bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="confirm-upload-button">
                {uploadLoading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
